// src/utils/quizUtility.jsx - NEW COURSEOBJECT STRUCTURE v2.0
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../components/firebase';


// Initialize user progress document if it doesn't exist
const initializeUserProgress = async (userId, userInfo = null) => {
    try {
        const userDocRef = doc(db, 'quizProgress', userId);
        const docSnap = await getDoc(userDocRef);

        if (!docSnap.exists()) {
            const initialData = {
                createdAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString(),
                courses: {} // Empty courses object
            };

            // Add user info if provided
            if (userInfo) {
                initialData.userName = userInfo.displayName || 'Unknown User';
                initialData.userEmail = userInfo.email || '';
            }

            await setDoc(userDocRef, initialData);
        } else {
            // Update existing document with user info if not present
            const existingData = docSnap.data();
            const updates = {};

            if (userInfo && (!existingData.userName || !existingData.userEmail)) {
                updates.userName = userInfo.displayName || 'Unknown User';
                updates.userEmail = userInfo.email || '';
            }

            // Migrate old structure to new courseObject structure if needed
            if (!existingData.courses) {
                updates.courses = {};

                // If old data exists, migrate it
                if (existingData.currentSlide !== undefined || existingData.quizAnswers) {
                    // Assume old data belongs to free course
                    updates.courses['free-course'] = {
                        currentSlide: existingData.currentSlide || 0,
                        quizAnswers: existingData.quizAnswers || {},
                        showResults: existingData.showResults || false,
                        completedAt: existingData.completedAt || null
                    };
                }

                // Clean up old fields
                updates.currentSlide = null;
                updates.quizAnswers = null;
                updates.showResults = null;
                updates.completedAt = null;
            }

            if (Object.keys(updates).length > 0) {
                updates.lastUpdated = new Date().toISOString();
                await setDoc(userDocRef, updates, { merge: true });
            }
        }
    } catch (error) {
        console.error('❌ Error initializing user progress:', error);
    }
};

// Save quiz progress to Firestore using courseObject structure
export const saveQuizProgress = async (userId, courseId, progressData) => {
    try {
        const userDocRef = doc(db, 'quizProgress', userId);

        // First ensure user document exists
        await initializeUserProgress(userId);

        // Get existing data
        const docSnap = await getDoc(userDocRef);
        let existingData = {};
        if (docSnap.exists()) {
            existingData = docSnap.data();
        }

        // Build the proper nested structure
        const courses = existingData.courses || {};
        courses[courseId] = {
            currentSlide: progressData.currentSlide || 0,
            quizAnswers: progressData.quizAnswers || {},
            showResults: progressData.showResults || false,
            completedAt: progressData.completedAt || null,
            lastAccessed: progressData.lastAccessed || null
        };

        const courseUpdate = {
            courses: courses,
            lastUpdated: new Date().toISOString()
        };

        await setDoc(userDocRef, courseUpdate, { merge: true });
    } catch (error) {
        console.error('❌ Error saving quiz progress:', error);
    }
};

// Load quiz progress from Firestore using courseObject structure
export const loadQuizProgress = async (userId, courseId) => {
    try {
        const userDocRef = doc(db, 'quizProgress', userId);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
            const userData = docSnap.data();

            // Check if using new courseObject structure
            if (userData.courses && userData.courses[courseId]) {
                const courseData = userData.courses[courseId];

                const returnData = {
                    currentSlide: courseData.currentSlide || 0,
                    quizAnswers: courseData.quizAnswers || {},
                    showResults: courseData.showResults || false,
                    completedAt: courseData.completedAt || null
                };
                return returnData;
            } else {
                // Check if this is old data that needs migration for free course
                if (courseId === 'free-course' && (userData.currentSlide !== undefined || userData.quizAnswers)) {
                    const legacyData = {
                        currentSlide: userData.currentSlide || 0,
                        quizAnswers: userData.quizAnswers || {},
                        showResults: userData.showResults || false,
                        completedAt: userData.completedAt || null
                    };

                    // Save to new structure and return
                    await saveQuizProgress(userId, courseId, legacyData);
                    return legacyData;
                }

                // Return default for new course
                const defaultData = {
                    currentSlide: 0,
                    quizAnswers: {},
                    showResults: false,
                    completedAt: null
                };
                return defaultData;
            }
        } else {
            await initializeUserProgress(userId);

            const defaultData = {
                currentSlide: 0,
                quizAnswers: {},
                showResults: false,
                completedAt: null
            };
            return defaultData;
        }
    } catch (error) {
        console.error('❌ Error loading quiz progress:', error);
        return {
            currentSlide: 0,
            quizAnswers: {},
            showResults: false,
            completedAt: null
        };
    }
};

// Mark quiz as completed using courseObject structure
export const markQuizCompleted = async (userId, courseId, quizData) => {
    try {
        const userDocRef = doc(db, 'quizProgress', userId);

        // Get existing data
        const docSnap = await getDoc(userDocRef);
        let existingData = {};
        if (docSnap.exists()) {
            existingData = docSnap.data();
        }

        // Build the proper nested structure
        const courses = existingData.courses || {};
        courses[courseId] = {
            currentSlide: quizData.currentSlide || 0,
            quizAnswers: quizData.quizAnswers || {},
            showResults: quizData.showResults || false,
            lastAccessed: quizData.lastAccessed || null,
            completedAt: new Date().toISOString()
        };

        const courseUpdate = {
            courses: courses,
            lastUpdated: new Date().toISOString()
        };

        await setDoc(userDocRef, courseUpdate, { merge: true });
    } catch (error) {
        console.error('❌ Error marking quiz as completed:', error);
    }
};

// Save individual slide progress using courseObject structure
export const saveSlideProgress = async (userId, courseId, currentSlide) => {
    try {
        // First, get the current document to preserve existing data
        const userDocRef = doc(db, 'quizProgress', userId);
        const docSnap = await getDoc(userDocRef);

        let existingData = {};
        if (docSnap.exists()) {
            existingData = docSnap.data();
        }

        // Build the proper nested structure
        const courses = existingData.courses || {};
        const courseData = courses[courseId] || {};

        // Update only the currentSlide
        courseData.currentSlide = currentSlide;
        courses[courseId] = courseData;

        const courseUpdate = {
            courses: courses,
            lastUpdated: new Date().toISOString()
        };

        await setDoc(userDocRef, courseUpdate, { merge: true });
    } catch (error) {
        console.error('❌ Error saving slide progress:', error);
        throw error; // Re-throw to let caller know it failed
    }
};

// Save quiz answer using courseObject structure
export const saveQuizAnswer = async (userId, courseId, questionId, answerIndex, isCorrect) => {
    try {
        // First get current document data
        const userDocRef = doc(db, 'quizProgress', userId);
        const docSnap = await getDoc(userDocRef);

        let existingData = {};
        if (docSnap.exists()) {
            existingData = docSnap.data();
        }

        // Build the proper nested structure
        const courses = existingData.courses || {};
        const courseData = courses[courseId] || {};
        const currentQuizAnswers = courseData.quizAnswers || {};

        // Update the specific quiz answer
        const updatedQuizAnswers = {
            ...currentQuizAnswers,
            [questionId]: {
                selected: answerIndex,
                correct: isCorrect
            }
        };

        courseData.quizAnswers = updatedQuizAnswers;
        courses[courseId] = courseData;

        const courseUpdate = {
            courses: courses,
            lastUpdated: new Date().toISOString()
        };

        await setDoc(userDocRef, courseUpdate, { merge: true });
    } catch (error) {
        console.error('❌ Error saving quiz answer:', error);
    }
};

// Clean up corrupted data (enhanced for courseObject structure)
export const cleanupCorruptedData = async (userId, courseId) => {
    try {

        const userDocRef = doc(db, 'quizProgress', userId);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
            const userData = docSnap.data();

            // Clean specific course data AND remove legacy fields
            const courseUpdate = {
                [`courses.${courseId}`]: {
                    currentSlide: 0,
                    quizAnswers: {},
                    showResults: false,
                    completedAt: null
                },
                // Clean up legacy fields by setting them to null
                currentSlide: null,
                quizAnswers: null,
                showResults: null,
                completedAt: null,
                lastUpdated: new Date().toISOString()
            };

            // Keep user info but clean everything else
            if (userData.userName) courseUpdate.userName = userData.userName;
            if (userData.userEmail) courseUpdate.userEmail = userData.userEmail;
            if (userData.createdAt) courseUpdate.createdAt = userData.createdAt;

            await setDoc(userDocRef, courseUpdate, { merge: true });
        }

        return true;
    } catch (error) {
        console.error('❌ CLEANUP: Error during cleanup:', error);
        return false;
    }
};